import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DateRangePicker.css';

interface DateRangePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ value, onChange, placeholder = 'Date Range' }) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (value) {
      const dates = value.split(' - ');
      if (dates.length === 2) {
        // Parse MM/DD/YYYY format
        const parseDate = (dateStr: string): Date | null => {
          const parts = dateStr.trim().split('/');
          if (parts.length === 3) {
            const month = parseInt(parts[0]) - 1;
            const day = parseInt(parts[1]);
            const year = parseInt(parts[2]);
            const date = new Date(year, month, day);
            if (!isNaN(date.getTime())) {
              return date;
            }
          }
          return null;
        };
        
        const start = parseDate(dates[0]);
        const end = parseDate(dates[1]);
        if (start && end) {
          setStartDate(start);
          setEndDate(end);
        }
      }
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  }, [value]);

  const formatDate = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    // Don't auto-apply, let user click Apply button
  };

  const displayValue = value || placeholder;

  return (
    <div className="date-range-picker-wrapper" ref={wrapperRef}>
      <div className="position-relative">
        <span className="svg-icon svg-icon-2 position-absolute mx-4 mt-4" style={{ zIndex: 1, left: 0 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path opacity="0.3" d="M21 22H3C2.4 22 2 21.6 2 21V5C2 4.4 2.4 4 3 4H21C21.6 4 22 4.4 22 5V21C22 21.6 21.6 22 21 22Z" fill="currentColor"></path>
            <path d="M6 6C5.4 6 5 5.6 5 5V3C5 2.4 5.4 2 6 2C6.6 2 7 2.4 7 3V5C7 5.6 6.6 6 6 6ZM11 5V3C11 2.4 10.6 2 10 2C9.4 2 9 2.4 9 3V5C9 5.6 9.4 6 10 6C10.6 6 11 5.6 11 5ZM15 5V3C15 2.4 14.6 2 14 2C13.4 2 13 2.4 13 3V5C13 5.6 13.4 6 14 6C14.6 6 15 5.6 15 5Z" fill="currentColor"></path>
          </svg>
        </span>
        <input
          className="form-control form-control-solid ps-12"
          type="text"
          placeholder={placeholder}
          value={displayValue}
          readOnly
          onClick={() => setIsOpen(!isOpen)}
          style={{ cursor: 'pointer' }}
        />
      </div>
      {isOpen && (
        <div className="date-range-picker-popup">
          <DatePicker
            selected={startDate}
            onChange={handleDateChange}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            inline
            monthsShown={2}
          />
          <div className="date-range-picker-actions">
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
                onChange('');
                setIsOpen(false);
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                if (startDate && endDate) {
                  const dateRange = `${formatDate(startDate)} - ${formatDate(endDate)}`;
                  onChange(dateRange);
                }
                setIsOpen(false);
              }}
              disabled={!startDate || !endDate}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;

